import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function FaqAssistant({ token }) {
  const { isMobile } = useIsMobile();
  const [query, setQuery] = useState('');
  const [moduleName, setModuleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [meta, setMeta] = useState({ total_accessible: 0, total_results: 0 });
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiConfidence, setAiConfidence] = useState('');
  const [aiSources, setAiSources] = useState([]);

  const loadFaq = async (nextQuery = query, nextModule = moduleName) => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams();
      if (String(nextQuery || '').trim()) q.set('query', String(nextQuery).trim());
      if (String(nextModule || '').trim()) q.set('module', String(nextModule).trim());
      q.set('limit', '8');

      const res = await fetch(`/api/faq/search?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to load FAQ');
        setResults([]);
        setSuggested([]);
        return;
      }
      setResults(Array.isArray(data.results) ? data.results : []);
      setSuggested(Array.isArray(data.suggested_questions) ? data.suggested_questions : []);
      setMeta({
        total_accessible: Number(data.total_accessible || 0),
        total_results: Number(data.total_results || 0)
      });
    } catch (_) {
      setError('Network error while loading FAQ');
      setResults([]);
      setSuggested([]);
    } finally {
      setLoading(false);
    }
  };

  const askAssistant = async (nextQuery = query, nextModule = moduleName) => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams();
      if (String(nextQuery || '').trim()) q.set('query', String(nextQuery).trim());
      if (String(nextModule || '').trim()) q.set('module', String(nextModule).trim());
      const res = await fetch(`/api/faq/answer?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to get assistant answer');
        return;
      }
      setAiAnswer(String(data.answer || '').trim());
      setAiConfidence(String(data.confidence || '').trim());
      setAiSources(Array.isArray(data.sources) ? data.sources : []);
      if (String(nextQuery || '').trim()) {
        await loadFaq(nextQuery, nextModule);
      }
    } catch (_) {
      setError('Network error while getting assistant answer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFaq('', '');
  }, [token]);

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>FAQ</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>FAQ Assistant</h2>
      <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.9rem' }}>
        Ask operational questions. Answers are filtered by your user access.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar">
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr auto', gap: '0.6rem', alignItems: 'center' }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask something like: how to settle bill, who can approve PO"
            style={{ width: '100%', padding: '8px' }}
          />
          <select value={moduleName} onChange={(e) => setModuleName(e.target.value)} style={{ width: '100%', padding: '8px' }}>
            <option value="">All Modules</option>
            <option value="FRONT_OFFICE">Front Office</option>
            <option value="HOUSEKEEPING">Housekeeping</option>
            <option value="KOT">KOT</option>
            <option value="BAR">BAR</option>
            <option value="FOOD">Food Inventory</option>
            <option value="BANQUET">Banquet</option>
            <option value="ACCOUNTING">Accounting</option>
            <option value="SECURITY">Security</option>
            <option value="GENERAL">General</option>
          </select>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => loadFaq(query, moduleName)}
              disabled={loading}
              className="btn-primary"
              style={{ padding: '0.65rem 1rem' }}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button
              type="button"
              onClick={() => askAssistant(query, moduleName)}
              disabled={loading}
              className="btn-success"
              style={{ padding: '0.65rem 1rem' }}
            >
              {loading ? 'Working...' : 'Ask Assistant'}
            </button>
          </div>
        </div>

        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
          Accessible FAQ items: {meta.total_accessible} | Matched: {meta.total_results}
        </div>
      </div>

      {aiAnswer ? (
        <div className="card" style={{ borderLeft: '4px solid #22c55e', marginBottom: '1rem' }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div style={{ fontWeight: 700, color: '#15803d' }}>Assistant Answer</div>
              <span className="tag tag-success" style={{ fontSize: '0.78rem' }}>Confidence: {aiConfidence || 'n/a'}</span>
            </div>
            <div style={{ color: '#1a202c', lineHeight: 1.6 }}>{aiAnswer}</div>
            {aiSources.length > 0 && (
              <div style={{ marginTop: '0.6rem', color: '#6b7280', fontSize: '0.85rem' }}>
                Sources: {aiSources.map((s) => `${s.module}: ${s.question}`).join(' | ')}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {suggested.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div className="section-head">Suggested Questions</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
            {suggested.map((q, idx) => (
              <button
                key={`${q}-${idx}`}
                type="button"
                onClick={() => {
                  setQuery(q);
                  loadFaq(q, moduleName);
                }}
                style={{ border: '1px solid #3b5bcc', background: '#eff3ff', borderRadius: '999px', padding: '0.3rem 0.7rem', color: '#3b5bcc', fontSize: '0.82rem', fontWeight: 500 }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {results.length === 0 ? (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#fafafa', borderRadius: '8px', border: '1px dashed var(--theme-border)' }}>No configured FAQ answer found for this query. Try broader keywords.</div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {results.map((item) => (
            <div key={item.id} className="card">
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', alignItems: 'center' }}>
                  <div style={{ fontWeight: 700, color: '#1f2937' }}>{item.question}</div>
                  <span className="tag tag-muted">{item.module}</span>
                </div>
                <div style={{ marginTop: '0.55rem', color: '#2f3640' }}>{item.answer}</div>
                {Array.isArray(item.tags) && item.tags.length > 0 && (
                  <div style={{ marginTop: '0.55rem', color: '#718096', fontSize: '0.82rem' }}>
                    Tags: {item.tags.join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FaqAssistant;
